"use client";

import clsx from "clsx";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { VoiceAccent, VoiceAge, VoiceGender } from "@/enums/agencies";
import { SupportedLanguages } from "@/enums/internationalization";
import { VoiceOption } from "@/types/agencies";

export interface VoiceFilters {
  languages: SupportedLanguages[];
  genders: VoiceGender[];
  ages: VoiceAge[];
  accents: VoiceAccent[];
}

export const DEFAULT_VOICE_FILTERS: VoiceFilters = {
  languages: [],
  genders: [],
  ages: [],
  accents: [],
};

export function matchesVoiceFilters(voice: VoiceOption, filters: VoiceFilters) {
  if (
    filters.languages.length > 0 &&
    !voice.supportedLanguages.some((lang) => filters.languages.includes(lang))
  ) {
    return false;
  }

  if (filters.genders.length > 0 && (!voice.labels.gender || !filters.genders.includes(voice.labels.gender))) {
    return false;
  }

  if (filters.ages.length > 0 && (!voice.labels.age || !filters.ages.includes(voice.labels.age))) {
    return false;
  }

  if (
    filters.accents.length > 0 &&
    (!voice.labels.accent || !filters.accents.includes(voice.labels.accent))
  ) {
    return false;
  }

  return true;
}

export function matchesExploreSearch(voice: VoiceOption, query: string) {
  if (!query.trim()) return true;
  const q = query.toLowerCase().trim();
  return (
    voice.name.toLowerCase().includes(q) ||
    Boolean(voice.labels.accent?.toLowerCase().includes(q)) ||
    voice.supportedLanguages.some((lang) => lang.toLowerCase().includes(q))
  );
}

export function countActiveVoiceFilters(filters: VoiceFilters) {
  return (
    filters.languages.length +
    filters.genders.length +
    filters.ages.length +
    filters.accents.length
  );
}

interface VoiceSelectorFiltersProps {
  filters: VoiceFilters;
  onFiltersChange: (filters: VoiceFilters) => void;
}

export default function VoiceSelectorFilters({
  filters,
  onFiltersChange,
}: Readonly<VoiceSelectorFiltersProps>) {
  const { t } = useTranslation("inbound");
  const { t: ct } = useTranslation("common");
  const { t: st } = useTranslation("settings");
  const [isOpen, setIsOpen] = useState(false);

  const languageOptions = useMemo(
    () =>
      Object.values(SupportedLanguages).map((lang) => ({
        value: lang,
        label: st(lang),
      })),
    [st]
  );

  const genderOptions = useMemo(
    () =>
      Object.values(VoiceGender).map((gender) => ({
        value: gender,
        label: st(gender),
      })),
    [st]
  );

  const ageOptions = useMemo(
    () =>
      Object.values(VoiceAge).map((age) => ({
        value: age,
        label: st(age),
      })),
    [st]
  );

  const accentOptions = useMemo(
    () =>
      Object.values(VoiceAccent).map((accent) => ({
        value: accent,
        label: st(accent),
      })),
    [st]
  );

  const handleLanguageChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      languages: values as SupportedLanguages[],
    });
  };

  const handleGenderChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      genders: values as VoiceGender[],
    });
  };

  const handleAgeChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      ages: values as VoiceAge[],
    });
  };

  const handleAccentChange = (values: string[]) => {
    onFiltersChange({
      ...filters,
      accents: values as VoiceAccent[],
    });
  };

  const activeFilterCount = useMemo(() => {
    return (
      filters.languages.length +
      filters.genders.length +
      filters.ages.length +
      filters.accents.length
    );
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  const handleClearFilters = () => {
    onFiltersChange(DEFAULT_VOICE_FILTERS);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={clsx(
            "inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors",
            hasActiveFilters
              ? "bg-primary/10 border-primary text-primary"
              : "bg-background border-neutral hover:border-primary/60 text-foreground"
          )}
        >
          <Icon icon="filter" />
          <span>{ct("filter")}</span>
          {hasActiveFilters && (
            <span className="flex justify-center items-center bg-primary rounded-full w-5 h-5 font-medium text-white text-xs">
              {activeFilterCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-4" portalToBody>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-foreground">{ct("filter")}</h4>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="text-muted-foreground text-sm hover:text-foreground"
              >
                {t("pages.agent.filters.clearFilters")}
              </button>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="block mb-1.5 text-muted-foreground text-sm">
                {t("pages.agent.filters.languages")}
              </label>
              <MultiSelect
                options={languageOptions}
                defaultValue={filters.languages}
                onValueChange={handleLanguageChange}
                placeholder={t("pages.agent.filters.allLanguages")}
                maxCount={2}
                hideSelectAll
                className="w-full"
                popoverClassName="!min-w-0 w-[var(--radix-popover-trigger-width)]"
              />
            </div>

            <div>
              <label className="block mb-1.5 text-muted-foreground text-sm">
                {t("pages.agent.filters.gender")}
              </label>
              <MultiSelect
                options={genderOptions}
                defaultValue={filters.genders}
                onValueChange={handleGenderChange}
                placeholder={t("pages.agent.filters.allGenders")}
                maxCount={2}
                hideSelectAll
                className="w-full"
                popoverClassName="!min-w-0 w-[var(--radix-popover-trigger-width)]"
              />
            </div>

            <div>
              <label className="block mb-1.5 text-muted-foreground text-sm">
                {t("pages.agent.filters.age")}
              </label>
              <MultiSelect
                options={ageOptions}
                defaultValue={filters.ages}
                onValueChange={handleAgeChange}
                placeholder={t("pages.agent.filters.allAges")}
                maxCount={2}
                hideSelectAll
                className="w-full"
                popoverClassName="!min-w-0 w-[var(--radix-popover-trigger-width)]"
              />
            </div>

            <div>
              <label className="block mb-1.5 text-muted-foreground text-sm">
                {t("pages.agent.filters.accent")}
              </label>
              <MultiSelect
                options={accentOptions}
                defaultValue={filters.accents}
                onValueChange={handleAccentChange}
                placeholder={t("pages.agent.filters.allAccents")}
                maxCount={2}
                hideSelectAll
                className="w-full"
                popoverClassName="!min-w-0 w-[var(--radix-popover-trigger-width)]"
              />
            </div>
          </div>

          <div className="pt-2 border-t">
            <Button type="primary" wide compact onClick={() => setIsOpen(false)}>
              {ct("done")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
